import { useCallback, useEffect, useRef } from "react";

import { track } from "../utils/analytics";

type Props = Record<string, unknown>;

/**
 * Funnel tracking for a form: `form_started` → `form_submitted`, with
 * `form_submit_failed` and `form_abandoned` as the two ways out.
 *
 * - `markDirty`: call on every change (e.g. Mantine's `onValuesChange`). The first call fires
 *   `form_started`.
 * - `trackSubmit`: call once the submit has actually succeeded.
 * - `trackInvalid`: pass as the second argument to Mantine's `form.onSubmit(valid, invalid)`.
 * - `trackFailure`: call when the server rejects or fails the submit.
 *
 * `form_abandoned` fires on unmount if the form was edited and never successfully submitted.
 */
export function useTrackForm(formName: string) {
  const dirty = useRef(false);
  const submitted = useRef(false);

  const markDirty = useCallback(() => {
    if (!dirty.current) {
      dirty.current = true;
      track("form_started", { form: formName });
    }
  }, [formName]);

  const trackSubmit = useCallback(
    (props: Props = {}) => {
      submitted.current = true;
      track("form_submitted", { form: formName, ...props });
    },
    [formName]
  );

  const trackInvalid = useCallback(
    (errors: Record<string, unknown>) => {
      track("form_submit_failed", {
        form: formName,
        reason: "validation",
        error_fields: Object.keys(errors),
      });
    },
    [formName]
  );

  const trackFailure = useCallback(
    (props: Props = {}) => {
      track("form_submit_failed", {
        form: formName,
        ...props,
        reason: "server",
      });
    },
    [formName]
  );

  const trackAbandon = useCallback(
    (props: Props = {}) => {
      track("form_abandoned", { form: formName, ...props });
    },
    [formName]
  );

  useEffect(() => {
    return () => {
      if (dirty.current && !submitted.current) {
        track("form_abandoned", { form: formName });
      }
    };
  }, [formName]);

  return { markDirty, trackSubmit, trackInvalid, trackFailure, trackAbandon };
}
